import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Avatar } from "./Avatar";
import { ChatCircle, Trash, Flag } from "@phosphor-icons/react";
import { REACTIONS, timeAgo } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";
import api, { mediaUrl } from "../lib/api";
import { toast } from "sonner";
import { ReportDialog } from "./ReportDialog";

const CHANNEL_NAMES = {
  general: "General", anxiety: "Anxiety", depression: "Depression",
  stress: "Stress & Burnout", relationships: "Relationships",
  loneliness: "Loneliness", gratitude: "Gratitude",
};

export const PostCard = ({ post, onChange, onDeleted, clickable = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(post);
  const [showReport, setShowReport] = useState(false);

  const react = async (e, type) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/posts/${data.id}/react`, { type });
      setData(res.data);
      onChange && onChange(res.data);
    } catch {
      toast.error("Could not register your reaction");
    }
  };

  const del = async (e) => {
    e.stopPropagation();
    try {
      await api.delete(`/posts/${data.id}`);
      toast.success("Post removed");
      onDeleted && onDeleted(data.id);
    } catch {
      toast.error("Could not delete post");
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => clickable && navigate(`/post/${data.id}`)}
      className={`bg-white rounded-[2rem] p-6 sm:p-8 shadow-[0_8px_30px_rgba(44,54,43,0.03)] border border-[#E6EBE4]/60 hover:shadow-[0_20px_40px_rgba(44,54,43,0.06)] transition-all duration-500 ${clickable ? "cursor-pointer" : ""}`}
      data-testid="post-card"
    >
      <div className="flex justify-between items-start mb-5">
        <div className="flex items-center gap-3">
          <Avatar handle={data.handle} size={44} />
          <div>
            <p className="font-semibold text-[#414C3F]" data-testid="post-handle">{data.handle}</p>
            <p className="text-xs text-[#8A9A86]">{timeAgo(data.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold bg-[#F4F1E8] text-[#6D7A6A] rounded-full px-3 py-1">
            {CHANNEL_NAMES[data.channel] || data.channel}
          </span>
          {user && user.id === data.author_id ? (
            <button onClick={del} className="text-[#C9B8A8] hover:text-[#D97B6A] transition-colors p-1" data-testid="delete-post-btn" title="Delete">
              <Trash size={18} weight="duotone" />
            </button>
          ) : user ? (
            <button
              onClick={(e) => { e.stopPropagation(); setShowReport(true); }}
              className="text-[#C9B8A8] hover:text-[#D97B6A] transition-colors p-1"
              data-testid="report-post-btn"
              title="Report"
            >
              <Flag size={18} weight="duotone" />
            </button>
          ) : null}
          {user && user.role === "admin" && user.id !== data.author_id && (
            <button onClick={del} className="text-[#C9B8A8] hover:text-[#D97B6A] transition-colors p-1" data-testid="admin-delete-post-btn" title="Remove (admin)">
              <Trash size={18} weight="duotone" />
            </button>
          )}
        </div>
      </div>

      <p className="text-[#515A4F] text-lg leading-relaxed mb-7 whitespace-pre-wrap break-words" data-testid="post-content">
        {data.content}
      </p>

      {data.images && data.images.length > 0 && (
        <div className={`grid gap-2 mb-7 ${data.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid="post-images">
          {data.images.map((img, i) => (
            <img
              key={i}
              src={mediaUrl(img)}
              alt="post attachment"
              className="w-full rounded-2xl object-cover max-h-96 border border-[#E6EBE4]"
              onClick={(e) => e.stopPropagation()}
            />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-5 border-t border-[#F4F1E8]">
        <div className="flex items-center gap-2 flex-wrap">
          {REACTIONS.map((r) => {
            const active = data.my_reactions?.includes(r.type);
            const count = data.reaction_counts?.[r.type] || 0;
            return (
              <button
                key={r.type}
                onClick={(e) => react(e, r.type)}
                className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-transform duration-300 hover:scale-105 active:scale-90 border"
                style={{
                  backgroundColor: active ? r.bg : "#fff",
                  color: active ? r.text : "#8A9A86",
                  borderColor: active ? r.border : "#EAE6D9",
                }}
                data-testid={`react-${r.type}-btn`}
              >
                <span className="text-base leading-none">{r.emoji}</span>
                {count > 0 && <span>{count}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5 text-[#8A9A86] text-sm font-semibold" data-testid="comment-count">
          <ChatCircle size={20} weight="duotone" />
          {data.comment_count || 0}
        </div>
      </div>

      <ReportDialog postId={data.id} open={showReport} onOpenChange={setShowReport} />
    </motion.article>
  );
};
